import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Check, XCircle, ArrowRight, Info } from "lucide-react";
import { APPROVED_MEM_PRICE } from "@/config/approvedPricing2026";

/**
 * PackageDetailsModal
 * -------------------
 * Opens over the landing page (no navigation) to show the full expanded
 * spec for a membership or programme. All copy comes from the package row
 * itself (admin-editable in /admin/packages). Never surfaces `internal_price`.
 *
 * Props:
 *   pkg           — the package row from GET /api/packages
 *   onClose       — close handler
 *   isLoggedIn    — used for the primary CTA label
 */
export default function PackageDetailsModal({ pkg, onClose, isLoggedIn }) {
  const dialogRef = useRef(null);

  // Close on Escape + trap focus lightly
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!pkg) return null;

  const isMembership = pkg.type === "membership";
  const showPrice = pkg.show_public_price !== false;
  const hiddenMsg = pkg.public_price_message || "Your clinician will recommend the most appropriate level of support following your assessment.";
  // RUN A (preview): approved membership prices are display-only. When the
  // approved price differs from the live checkout price, show the approved
  // price and disable the checkout CTA (non-transactional) so no one can
  // purchase at a mismatched price.
  const approvedPrice = isMembership ? APPROVED_MEM_PRICE[pkg.id] : undefined;
  const displayPrice = approvedPrice != null ? approvedPrice : pkg.price;
  const checkoutDisabled = approvedPrice != null && approvedPrice !== pkg.price;
  const priceLabel = showPrice
    ? `£${Math.round(displayPrice)}${isMembership ? " / month" : ""}`
    : hiddenMsg;

  const ctaText = pkg.cta_text || (isMembership ? (isLoggedIn ? "Subscribe" : "Create account & subscribe") : (showPrice ? "Get started" : "Book an assessment"));
  const ctaHref = pkg.cta_destination
    || (isMembership ? `/checkout/summary?package=${pkg.id}&plan=membership` : "/prices");

  const Section = ({ title, children, testid }) => (
    <div className="mt-6" data-testid={testid}>
      <div className="fl-label text-[#f47a20] mb-2">{title}</div>
      {children}
    </div>
  );

  const List = ({ items, positive = true, testid }) => (
    <ul className="space-y-1.5 text-sm" style={{ color: "var(--fl-text-2)" }} data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2">
          {positive
            ? <Check size={14} className="text-[#f47a20] mt-1 shrink-0" />
            : <XCircle size={14} className="text-zinc-400 mt-1 shrink-0" />}
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );

  return createPortal(
    <div
      className="fl-modal-shell fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="package-details-modal"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pkg-modal-title"
        className="fl-card fl-card-elevated w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <div className="min-w-0">
            {pkg.badge && (
              <span className="inline-block text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#f47a20] text-white mb-2" data-testid="pkg-modal-badge">
                {pkg.badge}
              </span>
            )}
            <div className="fl-label text-[#f47a20]">{isMembership ? "Membership" : "Programme"}</div>
            <h2 id="pkg-modal-title" className="fl-heading text-3xl sm:text-4xl mt-1">{pkg.name}</h2>
            {pkg.headline && <p className="fl-heading text-lg mt-2" style={{ color: "var(--fl-muted)" }}>{pkg.headline}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="fl-btn-ghost !p-2 shrink-0"
            data-testid="pkg-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 flex items-baseline gap-2">
          <span className={`fl-heading ${showPrice ? "text-4xl text-[#f47a20]" : "text-xl"}`} data-testid="pkg-modal-price">
            {priceLabel}
          </span>
        </div>

        {pkg.duration && (
          <div className="text-xs mt-2" style={{ color: "var(--fl-muted-2)" }} data-testid="pkg-modal-duration">
            Recommended duration · <b>{pkg.duration}</b>
          </div>
        )}

        {pkg.full_description && (
          <p className="mt-6 text-sm leading-relaxed" style={{ color: "var(--fl-text-2)" }} data-testid="pkg-modal-description">
            {pkg.full_description}
          </p>
        )}

        {pkg.best_for && (
          <Section title="Best for" testid="pkg-modal-best-for">
            <p className="text-sm" style={{ color: "var(--fl-text-2)" }}>{pkg.best_for}</p>
          </Section>
        )}

        {(pkg.what_client_receives || []).length > 0 && (
          <Section title="What you receive" testid="pkg-modal-receives">
            <List items={pkg.what_client_receives} testid="pkg-modal-receives-list" />
          </Section>
        )}

        {(pkg.how_it_works || []).length > 0 && (
          <Section title="How it works" testid="pkg-modal-how">
            <ol className="space-y-2 text-sm" style={{ color: "var(--fl-text-2)" }}>
              {pkg.how_it_works.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="fl-heading text-[#f47a20] text-sm min-w-[1.5rem]">0{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {(pkg.testing || []).length > 0 && (
          <Section title="Testing may include" testid="pkg-modal-testing">
            <List items={pkg.testing} testid="pkg-modal-testing-list" />
          </Section>
        )}

        {(pkg.does_not_include || []).length > 0 && (
          <Section title="Does not include" testid="pkg-modal-excludes">
            <List items={pkg.does_not_include} positive={false} testid="pkg-modal-excludes-list" />
          </Section>
        )}

        {pkg.eligibility && (
          <Section title="Eligibility" testid="pkg-modal-eligibility">
            <p className="text-sm" style={{ color: "var(--fl-text-2)" }}>{pkg.eligibility}</p>
          </Section>
        )}

        {(pkg.terms || []).length > 0 && (
          <Section title="Terms" testid="pkg-modal-terms">
            <List items={pkg.terms} testid="pkg-modal-terms-list" />
          </Section>
        )}

        {(pkg.important_notes || []).length > 0 && (
          <div className="mt-6 text-[13px] p-3 rounded-md flex gap-2" style={{ background: "color-mix(in oklab, #F59E0B 6%, transparent)", border: "1px solid #F59E0B44", color: "#B45309" }} data-testid="pkg-modal-important">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              <div className="fl-label mb-1 text-[#B45309]">Important</div>
              <ul className="space-y-1">
                {pkg.important_notes.map((n, i) => <li key={i}>· {n}</li>)}
              </ul>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-2 sticky bottom-0 -mx-6 sm:-mx-8 px-6 sm:px-8 pt-4 pb-2" style={{ background: "var(--fl-bg-2)", borderTop: "1px solid var(--fl-border)" }}>
          {checkoutDisabled ? (
            <>
              <span
                className="text-sm justify-center flex-1 inline-flex items-center gap-2 px-3 py-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 cursor-default"
                title="Preview only — online joining reopens once checkout is updated to the new price."
                data-testid="pkg-modal-cta-disabled"
              >
                Booking update in progress
              </span>
              <Link
                to={`/contact?topic=membership&package=${pkg.id}`}
                className="fl-btn-anim fl-btn-ghost text-sm justify-center flex-1"
                data-testid="pkg-modal-enquire"
              >
                Enquire about this plan <ArrowRight size={13} />
              </Link>
            </>
          ) : (
            <>
              <Link
                to={ctaHref}
                className="fl-btn-anim fl-btn text-sm justify-center flex-1"
                data-testid="pkg-modal-cta"
              >
                {ctaText} <ArrowRight size={13} />
              </Link>
              <button
                onClick={onClose}
                className="fl-btn-anim fl-btn-ghost text-sm justify-center flex-1"
                data-testid="pkg-modal-close-secondary"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
